import { DOMRectangle } from "@dnd-kit/dom/utilities";
import {
  type DragDropEventHandlers,
  DragDropProvider,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
} from "@dnd-kit/react";
import {
  ENVELOPE_DURATION_CAP_MINUTES,
  ENVELOPE_DURATION_INCREMENT_MINUTES,
  moveStep,
  PreconditionFailedError,
  reorderStep,
  resizeStep,
  StepPlacement,
  type StepPlacementProps,
} from "@repo/domain";
import { Button } from "@repo/ui/components/ui/button";
import { cn } from "@repo/ui/lib/utils";
import { ArrowDown, ArrowUp, ChevronsDown, ListPlus, Minus, Plus, Trash2 } from "lucide-react";
import { createContext, type ReactNode, use, useCallback, useMemo, useRef, useState } from "react";
import { DurationPicker } from "@/shared/forms/fields/duration-picker";
import { TimelineBlock, timelineBlockSurface } from "./block";
import { timelinePosition } from "./layout";
import { pixelsToMinutes, scale, snapMinutes, type TimelineDensity } from "./scale";
import { StepProcedureViewport } from "./step-procedure-viewport";
import { ACCENTS, VARIANTS } from "./tokens";
import { Timeline } from "./track";

export interface EditableTimelineStep {
  id: string;
  title: string;
  placement: StepPlacementProps;
  /** Procedures displayed within this step. */
  procedures: readonly { id: string; title: string; completed?: boolean }[];
  /** Added by an unsubmitted proposal. */
  proposed?: boolean;
}

type Gesture = { stepId: string; kind: "move" | "resize" };
export type StepTimelineGesture = Gesture | { stepId: string; kind: "reorder"; toIndex: number };
export type ProcedureTimelineGesture = { kind: "procedure"; stepId: string; procedureId: string };
type TimelineGesture = Gesture | ProcedureTimelineGesture;

/** Inset the painted card, not its authored placement, to separate adjacent steps. */
const BLOCK_GAP_PX = 4;

interface EditableTimelineState {
  /** Preview-aware: mid-drag this is the projected result, not the committed steps. */
  steps: readonly EditableTimelineStep[];
  /** What the caller authored, which the envelope control edits. */
  envelopeMinutes: number;
  /** `max(envelopeMinutes, contentEnd)` — what the grid has to paint. */
  extent: number;
  contentEnd: number;
  /** A refused gesture's reason. Never set while not `editable`. */
  error: string | null;
}

interface EditableTimelineActions {
  /** Toggles: selecting the already-selected step clears it, which is what `aria-pressed` promises. */
  select: (id: string) => void;
  /**
   * Escape. Ignored mid-drag, where Escape is dnd-kit's cancel. Reports whether
   * it cleared anything, so the key stops here only when it did something.
   */
  clearSelection: () => boolean;
  /** `null` when the caller owns no envelope — the control renders nothing rather than a dead one. */
  setEnvelope: ((minutes: number) => void) | null;
  /** `null` when the surface has no way to add work. Its own argument is null when no step is selected. */
  addProcedure: ((stepId: string | null) => void) | null;
  removeProcedure: ((procedureId: string) => void) | null;
  moveProcedure: ((procedureId: string, stepId: string) => void) | null;
  reorder: (stepId: string, direction: -1 | 1) => void;
  /** The drag-resize commit, for a picked duration: a refusal lands in `error` the same way. */
  resize: (stepId: string, minutes: number) => void;
}

interface EditableTimelineMeta {
  readOnly: boolean;
  density: TimelineDensity;
  editable: boolean;
  selectedId: string | null;
  scale: (minutes: number) => number;
}

interface EditableTimelineContextValue {
  state: EditableTimelineState;
  actions: EditableTimelineActions;
  meta: EditableTimelineMeta;
}

const EditableTimelineContext = createContext<EditableTimelineContextValue | null>(null);

export function useEditableTimeline(): EditableTimelineContextValue {
  const context = use(EditableTimelineContext);
  if (!context) throw new Error("useEditableTimeline must be used within an EditableTimelineProvider");
  return context;
}

export interface EditableTimelineProviderProps {
  steps: readonly EditableTimelineStep[];
  /** Authored envelope; the grid expands if a step extends beyond it. */
  envelopeMinutes: number;
  editable: boolean;
  /** Hide controls and prevent editing, even when editable is true. */
  readOnly?: boolean;
  density?: TimelineDensity;
  selectedId?: string | null;
  onSelectionChange?: (id: string | null) => void;
  /** Completed source gesture and settled list. Independent of selection; cancellation or refusal emits nothing. */
  onStepsChange: (change: { steps: EditableTimelineStep[]; gesture: StepTimelineGesture }) => void;
  /** Omit where the surface does not own the envelope; the stepper then renders nothing. */
  onEnvelopeChange?: (minutes: number) => void;
  /** Omit where the surface has no add-work affordance; the button then renders nothing.
   * `null` means no step was selected — add the procedure as a step of its own. */
  onAddProcedure?: (stepId: string | null) => void;
  onRemoveProcedure?: (procedureId: string) => void;
  onMoveProcedure?: (procedureId: string, stepId: string) => void;
  children: ReactNode;
}

/** Shape adaptation only: all timing decisions belong to the domain helpers. */
export function editTimelineSteps(
  steps: readonly EditableTimelineStep[],
  gesture: StepTimelineGesture,
  minutes: number,
) {
  const placed = steps.map((step) => ({
    ...step,
    placement: new StepPlacement(step.placement),
  }));
  const index = steps.findIndex((step) => step.id === gesture.stepId);
  const result =
    gesture.kind === "reorder"
      ? reorderStep(placed, index, gesture.toIndex)
      : gesture.kind === "move"
        ? moveStep(placed, index, minutes)
        : resizeStep(placed, index, minutes);
  return result.map(({ placement, ...step }) => ({
    ...step,
    placement: { offsetMinutes: placement.offsetMinutes, durationMinutes: placement.durationMinutes },
  }));
}

/**
 * Owns drag state and the dnd-kit runtime; hands the rest of the editor
 * assembly — chrome, notice, grid — to whatever children a caller composes.
 */
export function EditableTimelineProvider({
  steps,
  envelopeMinutes,
  editable: editingEnabled,
  readOnly = false,
  density = "comfortable",
  selectedId,
  onSelectionChange,
  onStepsChange,
  onEnvelopeChange,
  onAddProcedure,
  onRemoveProcedure,
  onMoveProcedure,
  children,
}: EditableTimelineProviderProps) {
  const editable = editingEnabled && !readOnly;
  const [preview, setPreview] = useState<EditableTimelineStep[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const drag = useRef<{
    gesture: Gesture;
    original: readonly EditableTimelineStep[];
    result: EditableTimelineStep[] | null;
    refused: boolean;
    /** Whether the press landed on the step that was already selected. */
    wasSelected: boolean;
  } | null>(null);
  const sensors = useMemo(
    () => [
      PointerSensor.configure({
        activatorElements: (source) =>
          source.data.kind === "move"
            ? [source.element?.closest("section") ?? source.element]
            : source.handle
              ? [source.handle]
              : [source.element],
      }),
      KeyboardSensor.configure({ offset: { x: 0, y: scale(ENVELOPE_DURATION_INCREMENT_MINUTES, density) } }),
    ],
    [density],
  );

  const onDragStart: DragDropEventHandlers<TimelineGesture>["onDragStart"] = ({ operation }, manager) => {
    // No `editable` guard: `EditableStep` passes `disabled: !editable` to both
    // `useDraggable` calls, and dnd-kit's pointer and keyboard sensors both bail
    // on a disabled source before any operation starts. Make a step draggable
    // while the timeline is locked and this — and the click path in
    // `EditableStep` that assumes it — stops holding.
    if (!operation.source) return;
    // feedback:none skips dnd-kit's measurement; KeyboardSensor still needs a shape.
    if (operation.source.element) manager.dragOperation.shape = new DOMRectangle(operation.source.element);
    if (operation.source.data.kind === "procedure") {
      setError(null);
      return;
    }
    const { stepId, kind } = operation.source.data;
    const wasSelected = selectedId === stepId;
    drag.current = { gesture: { stepId, kind }, original: steps, result: null, refused: false, wasSelected };
    setError(null);
    if (!wasSelected) onSelectionChange?.(stepId);
  };
  const onDragMove: DragDropEventHandlers<TimelineGesture>["onDragMove"] = (event) => {
    const session = drag.current;
    if (!session) return;
    const gesture = session.gesture;
    const step = session.original.find((entry) => entry.id === gesture.stepId);
    if (!step) return;
    // 0.3.x dispatches before updating position: pointer supplies `to`, keyboard `by`.
    const delta =
      (event.to?.y ?? event.operation.position.current.y + (event.by?.y ?? 0)) - event.operation.position.initial.y;
    const initial = gesture.kind === "move" ? step.placement.offsetMinutes : step.placement.durationMinutes;
    try {
      session.result = editTimelineSteps(
        session.original,
        gesture,
        snapMinutes(initial + pixelsToMinutes(delta, density)),
      );
      session.refused = false;
      setPreview(session.result);
      setError(null);
    } catch (cause) {
      if (!(cause instanceof PreconditionFailedError)) throw cause;
      session.refused = true;
      setError(cause.message);
    }
  };
  const onDragEnd: DragDropEventHandlers<TimelineGesture>["onDragEnd"] = ({ canceled, operation }) => {
    if (operation.source?.data.kind === "procedure") {
      const source = operation.source.data;
      const targetId = operation.target?.data.stepId;
      if (editable && !canceled && typeof targetId === "string" && targetId !== source.stepId) {
        onMoveProcedure?.(source.procedureId, targetId);
      }
      return;
    }
    const session = drag.current;
    drag.current = null;
    setPreview(null);
    if (canceled) setError(null);
    if (!editable || canceled || !session) return;
    // Compared, not merely present: a press jitters a pixel or two, which still
    // produces a result — identical to what was there. Committing that would
    // dirty the form and burn an undo slot for a gesture that moved nothing.
    const moved =
      !session.refused &&
      session.result?.some((step, index) => {
        const before = session.original[index];
        return (
          before?.id !== step.id ||
          before.placement.offsetMinutes !== step.placement.offsetMinutes ||
          before.placement.durationMinutes !== step.placement.durationMinutes
        );
      });
    if (moved && session.result) {
      onStepsChange({ steps: session.result, gesture: session.gesture });
      return;
    }
    // Nothing moved, so the gesture was a click: pressing the selected step clears it.
    if (session.wasSelected) onSelectionChange?.(null);
  };

  const displayed = editable ? (preview ?? steps) : steps;
  const contentEnd = Math.max(
    0,
    ...displayed.map((step) => step.placement.offsetMinutes + step.placement.durationMinutes),
  );
  const extent = Math.max(envelopeMinutes, contentEnd);

  return (
    <DragDropProvider<TimelineGesture>
      sensors={sensors}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
    >
      <EditableTimelineContext
        value={{
          state: { steps: displayed, envelopeMinutes, extent, contentEnd, error: editable ? error : null },
          actions: {
            select: (id) => {
              if (!readOnly) onSelectionChange?.(id === selectedId ? null : id);
            },
            clearSelection: () => {
              if (readOnly || drag.current || !selectedId) return false;
              onSelectionChange?.(null);
              return true;
            },
            setEnvelope: readOnly ? null : (onEnvelopeChange ?? null),
            addProcedure: readOnly ? null : (onAddProcedure ?? null),
            removeProcedure: readOnly ? null : (onRemoveProcedure ?? null),
            moveProcedure: readOnly ? null : (onMoveProcedure ?? null),
            reorder: (stepId, direction) => {
              if (!editable) return;
              const index = steps.findIndex((step) => step.id === stepId);
              const toIndex = index + direction;
              const adjacent = steps[toIndex];
              if (
                !adjacent ||
                index < 0 ||
                [steps[index], adjacent].some((step) => step?.procedures.some((procedure) => procedure.completed))
              )
                return;
              const gesture = { kind: "reorder", stepId, toIndex } as const;
              const next = editTimelineSteps(steps, gesture, 0);
              setError(null);
              onStepsChange({ gesture, steps: next });
            },
            resize: (stepId, minutes) => {
              if (!editable) return;
              const gesture = { kind: "resize", stepId } as const;
              try {
                const next = editTimelineSteps(steps, gesture, minutes);
                setError(null);
                onStepsChange({ gesture, steps: next });
              } catch (cause) {
                if (!(cause instanceof PreconditionFailedError)) throw cause;
                setError(cause.message);
              }
            },
          },
          meta: {
            density,
            editable,
            readOnly,
            selectedId: readOnly ? null : (selectedId ?? null),
            scale: (minutes) => scale(minutes, density),
          },
        }}
      >
        {children}
      </EditableTimelineContext>
    </DragDropProvider>
  );
}

/** `Envelope · N min` — the default header summary; a caller can write its own from `useEditableTimeline()` instead. */
export function EditableTimelineSummary({ className }: { className?: string }) {
  const { state } = useEditableTimeline();
  return <Timeline.Summary className={className}>Envelope · {state.envelopeMinutes} min</Timeline.Summary>;
}

/**
 * Envelope stepper. Shortening stops at the work already placed rather than
 * clamping silently, so the disabled `−` is what says the steps fill the visit.
 */
export function EditableTimelineEnvelope({ className }: { className?: string }) {
  const { state, actions, meta } = useEditableTimeline();
  const { setEnvelope } = actions;
  if (!setEnvelope) return null;
  const floor = Math.max(ENVELOPE_DURATION_INCREMENT_MINUTES, state.contentEnd);
  return (
    <fieldset
      aria-label="Visit envelope"
      className={cn("flex h-7 min-w-0 items-center gap-1 rounded-md border pl-2 pr-1", className)}
    >
      <span className="text-xs text-muted-foreground">Envelope</span>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label="Shorten the visit envelope"
        disabled={!meta.editable || state.envelopeMinutes - ENVELOPE_DURATION_INCREMENT_MINUTES < floor}
        onClick={() => setEnvelope(state.envelopeMinutes - ENVELOPE_DURATION_INCREMENT_MINUTES)}
      >
        <Minus className="size-3" />
      </Button>
      <DurationPicker
        label="Visit envelope"
        className="min-w-14 justify-center"
        value={state.envelopeMinutes}
        min={floor}
        disabled={!meta.editable}
        onChange={setEnvelope}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label="Lengthen the visit envelope"
        disabled={
          !meta.editable || state.envelopeMinutes + ENVELOPE_DURATION_INCREMENT_MINUTES > ENVELOPE_DURATION_CAP_MINUTES
        }
        onClick={() => setEnvelope(state.envelopeMinutes + ENVELOPE_DURATION_INCREMENT_MINUTES)}
      >
        <Plus className="size-3" />
      </Button>
    </fieldset>
  );
}

/** Adds to the selected step, or — with nothing selected — reports no target so the caller can append a step. */
export function EditableTimelineAddProcedure({
  className,
  label = "Add procedure",
}: {
  className?: string;
  label?: string;
}) {
  const { state, actions, meta } = useEditableTimeline();
  const { addProcedure } = actions;
  if (!addProcedure) return null;
  // Icon-only, so the label is the accessible name and the tooltip says which mode it is in.
  const target = state.steps.find((step) => step.id === meta.selectedId);
  const name = target ? `${label} to ${target.title}` : `${label} as a new step`;
  return (
    <Button
      type="button"
      size="icon-sm"
      aria-label={label}
      title={name}
      className={cn("size-7", className)}
      disabled={!meta.editable}
      onClick={() => addProcedure(meta.selectedId)}
    >
      {/* Not a bare plus: the envelope stepper's own `+` sits right beside it. */}
      <ListPlus className="size-4" />
    </Button>
  );
}

/** Feedback for a refused timing gesture. */
export function EditableTimelineNotice({ className }: { className?: string }) {
  const { state } = useEditableTimeline();
  if (!state.error) return null;
  return (
    <div className={cn("space-y-3", className)}>
      <output className="block text-xs text-destructive">{state.error}</output>
    </div>
  );
}

/** A step as a target for procedure drags. `surface` keeps ids unique per drawing of the step, as with `useTimelineProcedureDrag`. */
export function useTimelineStepDrop(stepId: string, surface = "grid") {
  const { meta, actions } = useEditableTimeline();
  return useDroppable({
    id: `procedure-step:${surface}:${stepId}`,
    data: { stepId },
    accept: (source) => source.type === "timeline-procedure" && source.data.stepId !== stepId,
    disabled: !meta.editable || !actions.moveProcedure,
  });
}

/** The envelope grid and its steps — the draggable surface itself. */
export function EditableTimelineGrid({
  className,
  renderStepContent,
}: {
  className?: string;
  renderStepContent?: (stepId: string) => ReactNode;
}) {
  const { state, actions, meta } = useEditableTimeline();
  const { steps, extent, contentEnd } = state;
  const { density } = meta;
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Escape is a shortcut for the focusable step handles inside, not a control of its own.
    <div
      className={cn("pb-5 pl-12", className)}
      onKeyDown={(event) => {
        // Escape clears the selection first and stops there. With nothing selected
        // it keeps bubbling, so the dialog the visit page opens this in still closes
        // rather than needing its own escape hatch.
        if (event.key === "Escape" && actions.clearSelection()) event.stopPropagation();
      }}
    >
      <section
        aria-label="Envelope grid"
        className="relative rounded-md bg-muted/40"
        style={{ height: scale(extent, density) }}
      >
        {Array.from({ length: Math.floor(extent / ENVELOPE_DURATION_INCREMENT_MINUTES) + 1 }, (_, index) => {
          const minutes = index * ENVELOPE_DURATION_INCREMENT_MINUTES;
          return (
            <div
              key={minutes}
              className="pointer-events-none absolute inset-x-0 border-t border-border/30"
              style={{ top: scale(minutes, density) }}
            >
              <span className="absolute -left-12 -top-2 w-10 text-right text-[10px] font-normal text-muted-foreground/60 tabular-nums">
                {minutes} min
              </span>
            </div>
          );
        })}
        {extent > contentEnd && (
          <section
            aria-label={`Unallocated envelope time starts at ${contentEnd} min`}
            className="pointer-events-none absolute inset-x-0 flex items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground"
            style={{ top: scale(contentEnd, density), height: scale(extent, density) - scale(contentEnd, density) }}
          >
            {extent - contentEnd} min unallocated
          </section>
        )}
        <div className="absolute inset-x-0 pt-1 text-xs text-muted-foreground" style={{ top: scale(extent, density) }}>
          End of visit
        </div>
        {steps.map((step) => (
          <EditableStep key={step.id} step={step} content={renderStepContent?.(step.id)} />
        ))}
      </section>
    </div>
  );
}

function EditableStep({ step, content }: { step: EditableTimelineStep; content?: ReactNode }) {
  const { state, meta, actions } = useEditableTimeline();
  const { density, editable, readOnly, selectedId } = meta;
  const stepIndex = state.steps.findIndex((item) => item.id === step.id);
  const canReorder = (direction: -1 | 1) => {
    const neighbor = state.steps[stepIndex + direction];
    return (
      editable && neighbor && ![step, neighbor].some((item) => item.procedures.some((procedure) => procedure.completed))
    );
  };
  const selected = selectedId === step.id;
  const [proceduresOverflow, setProceduresOverflow] = useState(false);
  const drop = useTimelineStepDrop(step.id);
  const move = useDraggable<Gesture>({
    id: `move:${step.id}`,
    data: { stepId: step.id, kind: "move" },
    disabled: !editable,
    feedback: "none",
  });
  const resize = useDraggable<Gesture>({
    id: `resize:${step.id}`,
    data: { stepId: step.id, kind: "resize" },
    disabled: !editable,
    feedback: "none",
  });
  const { ref: moveElementRef, handleRef: moveHandleRef } = move;
  const { ref: resizeElementRef, handleRef: resizeHandleRef } = resize;
  // Register each button as its own handle so icon descendants can activate it.
  const moveRef = useCallback(
    (element: HTMLButtonElement | null) => {
      moveElementRef(element);
      moveHandleRef(element);
    },
    [moveElementRef, moveHandleRef],
  );
  const resizeRef = useCallback(
    (element: HTMLButtonElement | null) => {
      resizeElementRef(element);
      resizeHandleRef(element);
    },
    [resizeElementRef, resizeHandleRef],
  );
  const bodyHeight = scale(step.placement.durationMinutes, density) - BLOCK_GAP_PX;
  // 16px heading + 12px subtitle + 12px padding + 2px border.
  const twoLine = bodyHeight >= 42;
  const procedureNames = step.procedures.map((procedure) => procedure.title).join(", ");
  const heading = !twoLine && procedureNames ? procedureNames : step.title;
  const surfaceVariant = step.proposed ? "proposed" : "scheduled";
  return (
    <section
      ref={drop.ref}
      aria-label={step.title}
      title={procedureNames || step.title}
      className={cn(
        "group absolute inset-x-0 rounded-md bg-card transition-[outline-color]",
        selected
          ? "z-10 outline-2 outline-primary outline-offset-2"
          : !readOnly && "hover:outline-1 hover:outline-primary/50",
        (move.isDragging || resize.isDragging) && "z-10",
        drop.isDropTarget && "z-20 ring-2 ring-primary ring-offset-2",
      )}
      style={timelinePosition(step.placement.offsetMinutes, step.placement.durationMinutes, density, BLOCK_GAP_PX)}
    >
      {!content && (
        <TimelineBlock
          variant={selected ? "selected" : step.proposed ? "proposed" : "scheduled"}
          className={cn(
            "pointer-events-none absolute inset-x-0 top-0 justify-start px-3 py-1.5",
            !twoLine && "py-0.5",
            !readOnly && !selected && "group-hover:bg-primary/10",
          )}
          style={{ height: bodyHeight }}
        >
          <TimelineBlock.Row>
            <TimelineBlock.Title className="font-semibold" title={heading}>
              {heading}
            </TimelineBlock.Title>
            <TimelineBlock.Duration>{step.placement.durationMinutes} min</TimelineBlock.Duration>
          </TimelineBlock.Row>
          {twoLine && procedureNames && procedureNames !== step.title && (
            <TimelineBlock.Detail className="leading-3" title={procedureNames}>
              {procedureNames}
            </TimelineBlock.Detail>
          )}
        </TimelineBlock>
      )}
      {content && (
        <div className={cn("absolute inset-0 flex flex-col", timelineBlockSurface({ variant: surfaceVariant }))}>
          <div
            className={cn(
              "flex h-6 shrink-0 items-center justify-between gap-2 px-2 text-[11px] font-medium",
              ACCENTS[VARIANTS[surfaceVariant].accent ?? "primary"].fillStrong,
            )}
          >
            <div className={cn("flex min-w-0 items-center gap-2", readOnly ? "pr-5" : "pr-24")}>
              <span>{step.title}</span>
              {editable ? (
                <DurationPicker
                  label={`${step.title} duration`}
                  className="relative z-30 -ml-1 h-5 text-[11px] text-muted-foreground"
                  value={step.placement.durationMinutes}
                  onChange={(minutes) => actions.resize(step.id, minutes)}
                />
              ) : (
                <span className="text-muted-foreground">{step.placement.durationMinutes} min</span>
              )}
            </div>
          </div>
          <StepProcedureViewport label={`${step.title} procedure list`} onOverflowChange={setProceduresOverflow}>
            {content}
          </StepProcedureViewport>
        </div>
      )}
      {!readOnly && (
        <>
          <button
            ref={moveRef}
            type="button"
            aria-label={`${editable ? "Move" : "Select"} ${step.title}`}
            aria-pressed={selected}
            // Only a locked timeline gets here: when `editable`, dnd-kit owns the
            // press (see `onDragStart`) and a moved drag has its click suppressed.
            onClick={editable ? undefined : () => actions.select(step.id)}
            className={cn(
              "absolute inset-0 z-10 h-full w-full rounded-md focus-visible:outline-2 focus-visible:outline-primary",
              editable && "touch-none cursor-grab active:cursor-grabbing",
            )}
          />
          <div className={cn("absolute top-0 z-30 flex", content && actions.addProcedure ? "right-7" : "right-1")}>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              className="size-6"
              disabled={!canReorder(-1)}
              aria-label={`Move ${step.title.toLowerCase()} up`}
              onClick={() => actions.reorder(step.id, -1)}
            >
              <ArrowUp className="size-3" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              className="size-6"
              disabled={!canReorder(1)}
              aria-label={`Move ${step.title.toLowerCase()} down`}
              onClick={() => actions.reorder(step.id, 1)}
            >
              <ArrowDown className="size-3" />
            </Button>
          </div>
        </>
      )}
      {content && proceduresOverflow && (
        <span
          role="img"
          aria-label="More procedures — scroll within this step"
          title="More procedures — scroll within this step"
          className={cn(
            "pointer-events-none absolute top-1.5 z-20 text-muted-foreground",
            readOnly ? "right-2" : "right-20",
          )}
        >
          <ChevronsDown className="size-3" />
        </span>
      )}
      {content && actions.addProcedure && (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="absolute right-1 top-0 z-30 size-6"
          disabled={!editable}
          aria-label={`Add procedure to ${step.title.toLowerCase()}`}
          onClick={() => actions.addProcedure?.(step.id)}
        >
          <Plus className="size-3" />
        </Button>
      )}
      {actions.removeProcedure && (
        <div className="absolute right-2 top-1 z-20 flex gap-1 rounded bg-card opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          {step.procedures
            .filter((procedure) => !procedure.completed)
            .map((procedure) => (
              <Button
                key={procedure.id}
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={`Remove ${procedure.title}`}
                title={`Remove ${procedure.title}`}
                className="text-destructive"
                disabled={!editable}
                onClick={() => actions.removeProcedure?.(procedure.id)}
              >
                <Trash2 className="size-3.5" />
              </Button>
            ))}
        </div>
      )}
      {editable && (
        <button
          ref={resizeRef}
          type="button"
          aria-label={`Resize ${step.title}`}
          className="absolute inset-x-0 bottom-0 z-20 h-1.5 w-full touch-none cursor-ns-resize rounded-b-md border-b-2 border-muted-foreground/40 focus-visible:outline-2 focus-visible:outline-primary"
        />
      )}
    </section>
  );
}
